import { Injector } from '@angular/core';
import { Subject } from 'rxjs';
import { ApplicationType, LanguageEnum, Permissions } from 'src/app/core/common';
import { LanguageService } from 'src/app/core/helper';
import { ComparisonStatus } from '../common';
import { UserPermissionService } from 'src/app/core/services/user-permission-service';
import { HttpHeaders } from '@angular/common/http';
import { FilterDropDown } from '../interfaces';

export function destroyStream(subject: Subject<boolean>): void {
  subject.next(true);
  subject.complete();
}

export function deleteObjectProperties(object: object, props: string[]): object {
  props.forEach((prop) => {
    if (object.hasOwnProperty(prop)) {
      delete object[prop];
    }
  });
  return object;
}

export function formatNumber(number: number, injector: Injector, format: boolean = true , translateNumbers : boolean = false): string {
  let enSymbols: string[] = ['', 'k', 'M', 'G', 'T', 'P', 'E'];
  let arSymbols: string[] = ['', 'الف', 'مليون', 'بليون', 'تريليون', 'P', 'E'];
  const languageService = injector.get(LanguageService);
  let symbols: string[] = [];
  if (languageService.currentLanguage$.value.isoCode == LanguageEnum.Arabic) {
    symbols = arSymbols;
  } else {
    symbols = enSymbols;
  }
  let numberOfZerosOverThree = (Math.log10(Math.abs(number)) / 3) | 0;

  if (numberOfZerosOverThree == 0 || format == false) {
    if (languageService.currentLanguage$.value.isoCode == LanguageEnum.Arabic && translateNumbers) {
      return number.toLocaleString('ar-EG', { useGrouping: false });
    } else {
      return number.toLocaleString('en-US', { useGrouping: false });
    }
  }

  // get suffix and determine scale
  let suffixSymbol = numberOfZerosOverThree <= symbols.length ? symbols[numberOfZerosOverThree] : '';
  let scale = Math.pow(10, numberOfZerosOverThree * 3);

  // scale the number
  let scaledNumber = number / scale;

  // format number and add suffix
  if (languageService.currentLanguage$.value.isoCode == LanguageEnum.Arabic) {
    return Number(scaledNumber.toFixed(1)).toLocaleString(translateNumbers ? 'ar-EG' : 'en-US' , { useGrouping: false }) + ` ${suffixSymbol}`;
  } else {
    return Number(scaledNumber.toFixed(1)).toLocaleString('en-US', { useGrouping: false }) + ` ${suffixSymbol}`;
  }
}

export function getCookie(cookieName: string): string | null {
  if (document.cookie.length != 0) {
    let cookieItem = document.cookie.split('; ');
    const cookieMap: Map<string, string> = new Map();
    cookieItem.forEach((item) => {
      let [key, value] = item.split('=');
      if (cookieName == key) {
        cookieMap.set(key, value);
      }
    });
    return cookieMap.get(cookieName);
  } else {
    return null;
  }
}

export function ComparisonOfTwoDates(firstDate: Date, secondDate: Date): ComparisonStatus {
  if (new Date(firstDate).getTime() == new Date(secondDate).getTime()) {
    return ComparisonStatus.EQUAL;
  } else if (new Date(firstDate).getTime() > new Date(secondDate).getTime()) {
    return ComparisonStatus.BIGGER;
  } else {
    return ComparisonStatus.SMALLER;
  }
}

export function localizeNumbers(number: number | string, language: LanguageEnum): string {
  if (!isNaN(+number)) {
    if (language == LanguageEnum.Arabic) {
      return number.toLocaleString('ar-EG', { useGrouping: false });
    } else {
      return number.toLocaleString('en-US', { useGrouping: false });
    }
  } else {
    return null;
  }
}

export function getNestedObjectValuesByString(object: object, keys: string): unknown {
  const keysArray: string[] = keys && keys.length > 0 ? keys.split('.') : [];
  let value: any = object;
  if (object && keysArray && keysArray.length > 0) {
    keysArray.forEach((key) => {
      value = value?.[key];
    });
  }

  return value;
}

export function DateFormatter(injector: Injector, date: string | Date, options?: object): string | null {
  const originalDate: Date = date ? new Date(date) : null;
  if (date) {
    const language: LanguageEnum = injector.get(LanguageService).currentLanguage$.value.isoCode;
    if (language == LanguageEnum.Arabic) {
      return originalDate.toLocaleDateString(
        'ar-EG',
        options
          ? options
          : {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }
      );
    } else {
      return originalDate.toLocaleDateString(
        'en-US',
        options
          ? options
          : {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }
      );
    }
  } else return null;
}

export function formatMobileNumber(number: string): string {
  return number.replace(/\D+/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '+($1) $2 $3 $4');
}

export function addDays(numberOfDays: number, date: Date = new Date()): Date {
  return new Date(new Date(date).getTime() + 1000 * 60 * 60 * 24 * numberOfDays);
}
export function subtractDays(numberOfDays: number, date: Date = new Date()): Date {
  return new Date(new Date(date).getTime() - 1000 * 60 * 60 * 24 * numberOfDays);
}

export function getObjectKeyByValue(object: object, value: string | number): string {
  return Object.keys(object).find((key) => object[key] === value);
}

export function checkIfNoValue(value: any) : boolean {
  if (value == null || value == undefined) {
    return false;
  } else {
    return true;
  }
}

export function getValidPermission(permissions: Permissions[], userPermissionService: UserPermissionService): boolean {
  return permissions.some(permission => userPermissionService.isHasPermission([permission]));
}

export function getSlotsIntoSpecificPeriod(slotPeriod: number = 1): Date[] {
  let array = []
  for (let hour = 0; hour < 24; hour += slotPeriod) {
    const formattedTime = new Date(new Date(0, 0, 0).getTime()+hour*60*60*1000)
    array.push(formattedTime);
  }
  return array;
}

export function TimeFormatter(injector: Injector, date: string | Date, options?: object): string | null {
  const language: string = injector.get(LanguageService).currentLanguage$.value.isoCode === 'ar' ? 'ar-EG' : 'en-US';
  const originalDate: Date = date ? new Date(date) : null;
  if (date) {
      return originalDate.toLocaleTimeString(
        language,
        options
          ? options
          : {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }
      );
  } else return null;
}

export function getNumberOfDigits(num: number): number {
  return num.toString().length;
}

export function formatDateWithSuffix(date: Date | string, injector: Injector): string {
  const language: string = injector.get(LanguageService).currentLanguage$.value.isoCode;
  const dateObject = new Date(date);
  const day = dateObject.getDate();
  const month = dateObject.toLocaleString(language == 'en' ? 'en-US' : 'ar-EG', { month: 'long' });
  const year = dateObject.getFullYear();
  const formattedYear = dateObject.toLocaleString(language === 'en' ? 'en-US' : 'ar-EG', { year: 'numeric' })
  const currentYear = new Date().getFullYear();
  let suffix: string;
  let formattedDate: string;
  if(language == 'en') {
    const lastDigit = day % 10;
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const exception = (day % 100 >= 11 && day % 100 <= 13) ? 0 : lastDigit;
    suffix = suffixes[exception] || suffixes[0];
    formattedDate = `${month} ${formatNumber(Number(day), injector, true, true)}${suffix}`
  } else {
    formattedDate = `${formatNumber(Number(day), injector, true, true)} ${month}`
  }
  return year !== currentYear ? `${formattedDate} ${formattedYear}` : formattedDate;
}

export function getCustomHeaders<T extends { [key: string]: any }>(
  defaultHeaders: HttpHeaders,
  newHeaders?: T
): { [key: string]: HttpHeaders } {
  let headersObject: { [key: string]: string | null } = {};
  //store default headers in a plain object
  defaultHeaders.keys().forEach((key) => {
    headersObject[key] = defaultHeaders.get(key);
  });
  //add new values to headers object
  if (newHeaders) {
    Object.keys(newHeaders).forEach((key) => {
      headersObject[key] = newHeaders[key];
    });
  }
  return {
    headers: new HttpHeaders(headersObject)
  };
}

export function getDropdownAllApplications(translationsList: object): FilterDropDown<ApplicationType>[] {
    return Object.values(ApplicationType).map((app) => {
      return {
        id: app,
        dropDownDisplayName: translationsList['ENUMS']['APPLICATION'][app?.toUpperCase()],
        dropDownValue: {
          id: app,
          dropDownDisplayName: translationsList['ENUMS']['APPLICATION'][app?.toUpperCase()],
        },
      };
    });
}